
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { format, isSameDay, startOfMonth, endOfMonth, eachDayOfInterval, addMonths, subMonths, startOfWeek, endOfWeek, addDays } from 'date-fns';
import { 
  Box, Card, CardContent, Typography, Chip, Button, CircularProgress,
  Grid, Avatar, FormControl, Select, MenuItem,
  IconButton, Tabs, Tab, Divider,Dialog, DialogContent  
} from '@mui/material';
import {
  Calendar as CalendarIcon, MapPin, User, Clock, DollarSign,
  ChevronLeft, ChevronRight, Building2, Bed, Users, TrendingUp
} from 'lucide-react';
import axios from 'axios';
import { KeyUri } from '../key';

const VendorBookingsCalendar = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [currentWeekStart, setCurrentWeekStart] = useState(startOfWeek(new Date())); 
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedHotel, setSelectedHotel] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [activeTab, setActiveTab] = useState('month');
  const [selectedBooking, setSelectedBooking] = useState(null);
const [detailOpen, setDetailOpen] = useState(false);

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const token = localStorage.getItem('vendorToken');
      const res = await axios.get(`${KeyUri.BACKEND_URI}/bookings/calendar`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookings(res.data.data);
    } catch (err) {
      console.error('Bookings fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const hotels = ['all', ...Array.from(new Set(bookings.map(b => b.hotelName)))];
  const statuses = ['all', 'confirmed', 'checked-in', 'checked-out'];

  const hotelsWithRooms = useMemo(() => {
    const hotelMap = {};
    
    bookings.forEach(booking => {
      if (!hotelMap[booking.hotelName]) {
        hotelMap[booking.hotelName] = {
          id: booking.hotelName.toLowerCase().replace(/\s+/g, '-'),
          name: booking.hotelName,
          location: booking.hotelLocation || 'Location not specified',
          roomTypes: {}
        };
      }

      const hotel = hotelMap[booking.hotelName];
      const roomKey = booking.roomType;
      
      if (!hotel.roomTypes[roomKey]) {
        hotel.roomTypes[roomKey] = {
          name: roomKey,
          totalRooms: 0,
          pricePerNight: booking.amount / Math.max(1, booking.nights || 1)
        };
      }
      hotel.roomTypes[roomKey].totalRooms += 1;
    });

    return Object.values(hotelMap).map(hotel => ({
      ...hotel,
      roomTypes: Object.values(hotel.roomTypes)
    }));
  }, [bookings]);

  const getBookingsForDate = (date) => {
    return bookings.filter(booking => {
      const isCheckInDay = isSameDay(booking.checkIn, date);
      const isCheckOutDay = isSameDay(booking.checkOut, date);
      const isActiveDuring = date >= booking.checkIn && date <= booking.checkOut;
      
      return (isCheckInDay || isCheckOutDay || isActiveDuring) &&
             (selectedHotel === 'all' || booking.hotelName === selectedHotel) &&
             (selectedStatus === 'all' || booking.status === selectedStatus);
    });
  };

  const datesWithBookings = bookings.reduce((dates, booking) => {
    const current = new Date(booking.checkIn);
    while (current <= booking.checkOut) {
      dates.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    return dates;
  }, []);

  const selectedDateBookings = getBookingsForDate(selectedDate);

  // Stats
  const totalBookings = bookings.length;
  const confirmedCount = bookings.filter(b => b.status === 'confirmed').length;
  const checkedInCount = bookings.filter(b => b.status === 'checked-in').length;
  const totalRevenue = bookings.reduce((sum, b) => sum + b.amount, 0);

  // Generate calendar days
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i));

  const filteredHotelsOccupancy = selectedHotel === 'all' 
    ? hotelsWithRooms 
    : hotelsWithRooms.filter(h => h.name === selectedHotel);

  const getOccupancyForDate = useCallback((date, hotelName, roomType) => {
    const dateBookings = bookings.filter(b => 
      isSameDay(b.checkIn, date) || (date >= b.checkIn && date <= b.checkOut)
    );
    return dateBookings.filter(b => b.hotelName === hotelName && b.roomType === roomType).length;
  }, [bookings]);

  const getOccupancyColor = (booked, total) => {
    const percentage = total > 0 ? (booked / total) * 100 : 0;
    if (percentage >= 90) return { bg: '#fef2f2', text: '#dc2626', border: '#fecaca' };
    if (percentage >= 70) return { bg: '#fffaf0', text: '#ea580c', border: '#fed7aa' };
    if (percentage >= 50) return { bg: '#fefce8', text: '#ca8a04', border: '#fef3c7' };
    return { bg: '#f0fdf4', text: '#166534', border: '#dcfce7' };
  };

  const getOccupancyLevel = (booked, total) => {
    const percentage = total > 0 ? (booked / total) * 100 : 0;
    if (percentage >= 90) return 'High';
    if (percentage >= 70) return 'Medium';
    if (percentage >= 50) return 'Low';
    return 'Available';
  };

  const handleViewDetails = (booking) => {
  setSelectedBooking(booking);
  setDetailOpen(true);
};
  return (
    <Box sx={{ p: 4, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
            Bookings Calendar
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View and manage room bookings across all properties
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<CalendarIcon style={{ fontSize: 18 }} />}>
          New Booking
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <Card sx={{ height: 100 }}>
            <CardContent sx={{ pt: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Total Bookings</Typography>
                  <Typography variant="h4">{totalBookings}</Typography>
                </Box>
                <Avatar sx={{ bgcolor: '#dbeafe', width: 40, height: 40 }}>
                  <CalendarIcon style={{ fontSize: 20, color: '#2563eb' }} />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <Card sx={{ height: 100 }}>
            <CardContent sx={{ pt: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Confirmed</Typography>
                  <Typography variant="h4">{confirmedCount}</Typography>
                </Box>
                <Avatar sx={{ bgcolor: '#dcfce7', width: 40, height: 40 }}>
                  <Clock style={{ fontSize: 20, color: '#16a34a' }} />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <Card sx={{ height: 100 }}>
            <CardContent sx={{ pt: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Checked In</Typography>
                  <Typography variant="h4">{checkedInCount}</Typography>
                </Box>
                <Avatar sx={{ bgcolor: '#f3e8ff', width: 40, height: 40 }}>
                  <User style={{ fontSize: 20, color: '#9333ea' }} />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <Card sx={{ height: 100 }}>
            <CardContent sx={{ pt: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Revenue</Typography>
                  <Typography variant="h4">₹{totalRevenue.toLocaleString()}</Typography>
                </Box>
                <Avatar sx={{ bgcolor: '#fed7aa', width: 40, height: 40 }}>
                  <DollarSign style={{ fontSize: 20, color: '#ea580c' }} />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tab Navigation */}
      <Card sx={{ mb: 4 }}>
        <CardContent sx={{ p: 3 }}>
          <Tabs 
            value={activeTab} 
            onChange={(_, newValue) => setActiveTab(newValue)}
            sx={{ mb: 2 }}
          >
            <Tab label="Monthly Calendar" value="month" />
            <Tab label="Occupancy View" value="occupancy" />
          </Tabs>
          <Divider />
        </CardContent>
      </Card>
      
      {/* Main Layout */}
      {activeTab === 'month' ? (
        <Grid container spacing={3}>
          {/* Pure MUI Calendar */}
          <Grid item xs={12} lg={4}>
            <Card>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ p: 3, pb: 1 }}>
                  <Typography variant="h6" gutterBottom>Calendar</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Select a date to view bookings
                  </Typography>
                </Box>

                {/* Month Navigation */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 3, pb: 2 }}>
                  <IconButton onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>
                    <ChevronLeft />
                  </IconButton>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {format(currentMonth, 'MMMM yyyy')}
                  </Typography>
                  <IconButton onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
                    <ChevronRight />
                  </IconButton>
                </Box>

                {/* Days Header */}
                <Grid container sx={{ px: 3, pb: 2 }}>
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                    <Grid key={day} item xs={12/7} textAlign="center" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                      {day}
                    </Grid>
                  ))}
                </Grid>

                {/* Calendar Days */}
                <Grid container sx={{ px: 3, pb: 3 }}>
                  {days.map((day) => {
                    const hasBookings = datesWithBookings.some(d => isSameDay(d, day));
                    const dayBookings = getBookingsForDate(day);
                    const isSelected = isSameDay(day, selectedDate);

                    return (
                      <Grid 
                        key={day.toString()} 
                        item 
                        xs={12/7} 
                        p={0.5}
                        onClick={() => setSelectedDate(day)}
                        sx={{ 
                          cursor: 'pointer',
                          borderRadius: 1,
                          p: 1.5,
                          bgcolor: isSelected ? 'primary.main' : 'transparent',
                          color: isSelected ? 'white' : 'inherit',
                          '&:hover': { bgcolor: 'action.hover' },
                          position: 'relative',
                          minHeight: 50,
                          display: 'flex',
                          flexDirection: 'column'
                        }}
                      >
                        <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {format(day, 'd')}
                        </Typography>
                        {hasBookings && (
                          <Chip 
                            label={dayBookings.length} 
                            size="small" 
                            sx={{ 
                              height: 16, 
                              fontSize: '0.6rem',
                              position: 'absolute',
                              top: 2,
                              right: 2,
                              bgcolor: dayBookings.length > 3 ? 'error.main' : 'warning.main',
                              color: 'white'
                            }} 
                          />
                        )}
                      </Grid>
                    );
                  })}
                </Grid>

                {/* Legend */}
                <Box sx={{ p: 3, bgcolor: '#f8fafc', borderTop: 1, borderColor: 'divider' }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1, display: 'block' }}>
                    Legend:
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 12, height: 12, bgcolor: '#2563eb', borderRadius: '50%' }} />
                    <Typography variant="caption">Dates with bookings</Typography>
                  </Box>
                </Box>

                {/* Filters */}
                <Box sx={{ p: 3 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', mb: 2, display: 'block' }}>
                    Filter by Hotel
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select value={selectedHotel} onChange={(e) => setSelectedHotel(e.target.value)}>
                      {hotels.map((hotel) => (
                        <MenuItem key={hotel} value={hotel}>
                          {hotel === 'all' ? 'All Hotels' : hotel}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  <Typography variant="caption" sx={{ color: 'text.secondary', mt: 2, mb: 1, display: 'block' }}>
                    Filter by Status
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
                      {statuses.map((status) => (
                        <MenuItem key={status} value={status}>
                          {status === 'all' ? 'All Statuses' : status.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Bookings List - UNCHANGED */}
          <Grid item xs={12} lg={8}>
            <Card>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ p: 3, pb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="h6">
                        Bookings for {format(selectedDate, 'MMMM d, yyyy')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedDateBookings.length} {selectedDateBookings.length === 1 ? 'booking' : 'bookings'} found
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton size="small">
                        <ChevronLeft />
                      </IconButton>
                      <IconButton size="small">
                        <ChevronRight />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>

                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                  </Box>
                ) : selectedDateBookings.length === 0 ? (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    py: 12, 
                    color: 'text.secondary' 
                  }}>
                    <CalendarIcon sx={{ 
                      fontSize: 48, 
                      opacity: 0.3, 
                      mb: 2, 
                      width: 48, 
                      height: 48 
                    }} />
                    <Typography variant="h6">No bookings for this date</Typography>
                  </Box>
                ) : (
                  <Box sx={{ p: 3 }}>
                    {selectedDateBookings.map((booking) => (
                      <Card key={booking.id} sx={{ mb: 2, border: '2px solid', borderColor: 'divider' }}>
                        <CardContent sx={{ pt: 3, pb: 3 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 3 }}>
                            <Box sx={{ flex: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <Chip
                                  label={booking.status.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                  size="small"
                                  sx={{
                                    bgcolor: booking.status === 'confirmed' ? '#dbeafe' :
                                             booking.status === 'checked-in' ? '#dcfce7' : '#f1f5f9',
                                    color: booking.status === 'confirmed' ? '#1e40af' :
                                           booking.status === 'checked-in' ? '#166534' : '#475569',
                                    '& .MuiChip-label': { fontSize: '0.75rem' }
                                  }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                  #{booking.id.slice(-6)}
                                </Typography>
                              </Box>
                              
                              <Box sx={{ mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                  <Building2 style={{ fontSize: 16, color: 'text.secondary' }} />
                                  <Typography variant="body2">{booking.hotelName}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                  <MapPin style={{ fontSize: 16, color: 'text.secondary' }} />
                                  <Typography variant="body2" color="text.secondary">
                                    {booking.hotelLocation}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                  <User style={{ fontSize: 16, color: 'text.secondary' }} />
                                  <Typography variant="body2" color="text.secondary">
                                    {booking.guestName} • {booking.numberOfGuests} guest{booking.numberOfGuests !== 1 ? 's' : ''}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <CalendarIcon style={{ fontSize: 16, color: 'text.secondary' }} />
                                  <Typography variant="body2" color="text.secondary">
                                    {format(booking.checkIn, 'MMM d')} - {format(booking.checkOut, 'MMM d')}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                            
                            <Box sx={{ textAlign: 'right' }}>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {booking.roomType}
                              </Typography>
                              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                ₹{booking.amount.toLocaleString()}
                              </Typography>
                              <Button 
  variant="outlined" 
  size="small" 
  onClick={() => handleViewDetails(booking)} 
  sx={{ mt: 2 }}
>
  View Details
</Button>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      ) : (
        /* ✅ Figma-Style OCCUPANCY VIEW */
        <Grid item xs={12}>
          <Box sx={{ 
            p: 4, 
            bgcolor: 'grey.50', 
            borderRadius: 2, 
            border: '1px solid', 
            borderColor: 'divider'
          }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  Booking Calendar
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Room availability and bookings across all properties
                </Typography>
              </Box>
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <Select value={selectedHotel} onChange={(e) => setSelectedHotel(e.target.value)}>
                  <MenuItem value="all">All Hotels</MenuItem>
                  {hotelsWithRooms.map((hotel) => (
                    <MenuItem key={hotel.id} value={hotel.name}>
                      {hotel.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Navigation */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {format(currentWeekStart, 'MMMM d')} - {format(endOfWeek(currentWeekStart), 'MMMM d, yyyy')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button 
                  variant="outlined" 
                  size="small"
                  onClick={() => setCurrentWeekStart(startOfWeek(new Date()))}
                  sx={{ textTransform: 'none', px: 2 }}
                >
                  Today
                </Button>
                <IconButton 
                  onClick={() => setCurrentWeekStart(addDays(currentWeekStart, -7))}
                  sx={{ bgcolor: 'white', border: '1px solid', borderColor: 'divider' }}
                >
                  <ChevronLeft style={{ fontSize: 16 }} />
                </IconButton>
                <IconButton 
                  onClick={() => setCurrentWeekStart(addDays(currentWeekStart, 7))}
                  sx={{ bgcolor: 'white', border: '1px solid', borderColor: 'divider' }}
                >
                  <ChevronRight style={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            </Box>

            {/* Occupancy Grid */}
            <Box sx={{ overflowX: 'auto', mb: 6 }}>
              <Box sx={{ minWidth: 1400 }}>
                {/* Header Row */}
                <Grid container sx={{ mb: 4 }}>
                  <Grid item xs={2} sx={{ p: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                      Hotel / Room Type
                    </Typography>
                  </Grid>
                  {weekDays.map((day) => (
                    <Grid key={day.toString()} item xs={1.428} sx={{ textAlign: 'center' }}>
                      <Box sx={{ 
                        p: 2, 
                        borderRadius: 1.5,
                        bgcolor: isSameDay(day, new Date()) ? '#dbeafe' : '#f8fafc',
                        mx: 0.5
                      }}>
                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                          {format(day, 'EEE')}
                        </Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 0.5 }}>
                          {format(day, 'd')}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                {/* Hotels */}
                {filteredHotelsOccupancy.map((hotel) => (
                  <>
                    {/* Hotel Header */}
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 2, 
                      mb: 3, 
                      pb: 2, 
                      borderBottom: 1, 
                      borderColor: 'divider'
                    }}>
                      <Building2 style={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {hotel.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        • {hotel.location}
                      </Typography>
                    </Box>

         
{hotel.roomTypes.map((roomType) => (
  <Box key={roomType.name} sx={{ mb: 2.5 }}>
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'stretch', 
      gap: 0.75, 
      flexWrap: 'nowrap !important',  
      minWidth: '100%'  
    }}>
      
      <Box sx={{ 
        flexShrink: 0,
        width: { xs: '28%', sm: 200, md: 220 }, 
        p: { xs: 1.75, md: 3 }, 
        bgcolor: 'grey.50', 
        borderRadius: { xs: '8px 4px 4px 8px', md: '12px 0 0 12px' },
        height: { xs: 72, md: 84 },
        display: 'flex', 
        flexDirection: 'column', 
        justifyContent: 'center',
        border: '1px solid',
        borderColor: 'divider'
      }}>
        <Typography noWrap variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>
          {roomType.name}
        </Typography>
        <Typography noWrap variant="caption" color="text.secondary">
          {roomType.totalRooms}rms
        </Typography>
      </Box>
      
    
      <Box sx={{ 
        display: 'flex', 
        flex: 1, 
        gap: 0.75,
        minWidth: 0 
      }}>
        {weekDays.map((day, dayIndex) => {
          const bookedRooms = getOccupancyForDate(day, hotel.name, roomType.name);
          const totalRooms = roomType.totalRooms;
          const colors = getOccupancyColor(bookedRooms, totalRooms);
          
          return (
            <Box
              key={day.toString()}
              sx={{
                flex: `1 1 ${100/7}%`,  
                height: { xs: 72, md: 84 },
                p: { xs: 1.25, md: 2 },
                borderRadius: dayIndex === 6 ? { xs: '4px 8px 8px 4px', md: '0 12px 12px 0' } : 1,
                border: '2px solid',
                borderColor: colors.border,
                bgcolor: colors.bg,
                color: colors.text,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                fontWeight: 700,
                fontSize: { xs: '0.9375rem', md: '1.125rem' },
                '&:hover': {
                  transform: 'translateY(-1px) !important',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.12)'
                },
                ...(isSameDay(day, new Date()) && {
                  borderColor: 'primary.main !important',
                  bgcolor: 'primary.50'
                })
              }}
            >
              <Typography sx={{ lineHeight: 1.1, mb: 0.125 }}>
                {bookedRooms}/{totalRooms}
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '0.6875rem' }}>
                {Math.round((bookedRooms/totalRooms)*100)}%
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  </Box>
))}

                  </>
                ))}
              </Box>
            </Box>

            {/* Legend */}
            <Box sx={{ pt: 4, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}>
                Occupancy Level:
              </Typography>
              <Box sx={{ display: 'flex', gap: 6 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ width: 16, height: 16, bgcolor: '#f0fdf4', border: '2px solid #dcfce7', borderRadius: 0.5 }} />
                  <Typography variant="caption">&lt; 50%</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ width: 16, height: 16, bgcolor: '#fefce8', border: '2px solid #fef3c7', borderRadius: 0.5 }} />
                  <Typography variant="caption">50-70%</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ width: 16, height: 16, bgcolor: '#fffaf0', border: '2px solid #fed7aa', borderRadius: 0.5 }} />
                  <Typography variant="caption">70-90%</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ width: 16, height: 16, bgcolor: '#fef2f2', border: '2px solid #fecaca', borderRadius: 0.5 }} />
                  <Typography variant="caption">≥ 90%</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Grid>
      )}
{/*
{detailOpen && selectedBooking && (
  <Dialog 
    open={detailOpen} 
    onClose={() => setDetailOpen(false)}
    maxWidth="md" 
    fullWidth 
    PaperProps={{ sx: { borderRadius: 3 } }}
  >
    <DialogContent sx={{ p: 0 }}>
      <Card sx={{ mx: 2, mt: 2, mb: 1, borderRadius: 3 }}>
        <CardContent sx={{ p: 4 }}>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                Booking #{selectedBooking.id?.slice(-6) || 'N/A'}
              </Typography>
              <Chip
                label={selectedBooking.status?.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown'}
                size="small"
                sx={{
                  bgcolor: selectedBooking.status === 'confirmed' ? '#dbeafe' :
                          selectedBooking.status === 'checked-in' ? '#dcfce7' : '#f1f5f9',
                  color: selectedBooking.status === 'confirmed' ? '#1e40af' : '#166534'
                }}
              />
            </Box>
            <IconButton onClick={() => setDetailOpen(false)} sx={{ ml: 2 }}>
              <ChevronLeft />
            </IconButton>
          </Box>

          <Grid container spacing={4}>
           
            <Grid item xs={12} md={7}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'text.secondary' }}>
                  Hotel Details
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Avatar sx={{ bgcolor: '#dbeafe', width: 48, height: 48 }}>
                    <Building2 style={{ fontSize: 20, color: '#2563eb' }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h6">{selectedBooking.hotelName}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedBooking.hotelLocation}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: '#f3e8ff', width: 40, height: 40 }}>
                    <User style={{ fontSize: 18, color: '#9333ea' }} />
                  </Avatar>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {selectedBooking.guestName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedBooking.numberOfGuests} guest{selectedBooking.numberOfGuests > 1 ? 's' : ''}
                    </Typography>
                  </Box>
                </Box>
              </Box>

             
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'text.secondary' }}>
                  Stay Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box sx={{ p: 2.5, bgcolor: '#f8fafc', borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Check-in
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {format(selectedBooking.checkIn, 'MMM d, yyyy')}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ p: 2.5, bgcolor: '#f8fafc', borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Check-out
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {format(selectedBooking.checkOut, 'MMM d, yyyy')}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
                <Box sx={{ mt: 3, p: 2.5, bgcolor: '#f0f9ff', borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    Room: {selectedBooking.roomType}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {Math.ceil((selectedBooking.checkOut - selectedBooking.checkIn) / (1000*60*60*24))} nights
                  </Typography>
                </Box>
              </Box>
            </Grid>

     
            <Grid item xs={12} md={5}>
              <Box sx={{ position: 'sticky', top: 24 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 3, color: 'text.secondary' }}>
                  Payment Summary
                </Typography>
                <Card sx={{ p: 3, mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Total Amount</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      ₹{selectedBooking.amount?.toLocaleString() || 0}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>
                      Paid
                    </Typography>
                    <Chip label="PAID" color="success" size="small" />
                  </Box>
                </Card>

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button 
                    fullWidth 
                    variant="outlined" 
                    onClick={() => setDetailOpen(false)}
                    startIcon={<ChevronLeft />}
                  >
                    Back to Calendar
                  </Button>
                  <Button fullWidth variant="contained" color="primary">
                    Manage Booking
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </DialogContent>
  </Dialog>
)}*/}

    </Box>
  );
  
};

export default VendorBookingsCalendar;
